import React, { useRef, useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ActionSection,
    ScrollableContent,
    MobileIcon,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";

const EditBasicInfo = ({ handleClose, key, selectedData, onSubmit, editDealBasicMutate, editDealBasicLoading }) => {
    let formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [key]);

    // Parse selectedData to populate initial values
    const getInitialValues = () => {
        if (!selectedData) {
            return {
                name: "",
                mobile: "",
                budget: "",
                alternateMobile: "",
            };
        }


        return {
            name: selectedData.name || "",
            mobile: selectedData.mobile || "",
            alternateMobile: selectedData.alternateMobile || "",
            budget: selectedData.budget || "",
        };
    };

    // Base validation schema
    const baseValidationSchema = Yup.object({
        name: Yup.string()
            .required('Client name is required')
            .matches(/^[A-Za-z0-9.\s]+$/, 'Only letters, numbers, spaces and dot are allowed.')
            .min(3, 'Name must be at least 3 characters long'),
        mobile: Yup.string()
            .required('Mobile number is required')
            .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits'),
        alternateMobile: Yup.string()
            .nullable()
            .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits'),

        budget: Yup.string()
            .required('Budget is required')
            .test('budget-range', 'Please enter a valid budget amount', function (value) {
                if (!value) return false;
                const numValue = parseInt(value);
                return numValue > 0 && numValue <= 100000000;
            }),
    });


    const handleSubmit = (values) => {

        // const payload = {
        //     dealId: selectedData?.id,
        //     name: values.name,
        //     mobile: values.mobile,
        //     alternateMobile: values.alternateMobile,
        //     budget: values.budget,
        // };

        // if (onSubmit) {
        //     onSubmit(payload);
        // }
        editDealBasicMutate({
            dealId: selectedData?.id,
            name: values.name,
            mobile: values.mobile,
            alternateMobile: values.alternateMobile,
            budget: values.budget,
        });
        // handleClose();
    };

    return (
        <Container style={{ height: `${height}px`, overflow: "auto" }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Edit Basic Info</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    innerRef={formikRef}
                    initialValues={getInitialValues()}
                    onSubmit={handleSubmit}
                    validationSchema={baseValidationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                >
                    {({
                        values,
                        setFieldValue,
                        handleChange,
                        handleBlur,
                        errors,
                        touched,
                        resetForm,
                    }) => {

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-180px)]">
                                    <Wrapper>
                                        {/* Client Name */}
                                        <InputGroup>
                                            <Label htmlFor="name">Client Name *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    className="w-full truncate"
                                                    placeholder="Enter client name"
                                                    value={values.name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </FieldWrappers>
                                            {errors.name && touched.name && (
                                                <ErrorText>{errors.name}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Client Mobile */}
                                        <InputGroup>
                                            <Label htmlFor="mobile">Mobile No. *</Label>
                                            <FieldWrappers>
                                                <MobileIcon />
                                                <Field
                                                    type="text"
                                                    id="mobile"
                                                    name="mobile"
                                                    className="w-full truncate pl-5"
                                                    placeholder="Enter 10-digit mobile number"
                                                    value={values.mobile}
                                                    maxLength={10}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d{0,10}$/.test(value)) {
                                                            setFieldValue("mobile", value);
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                />
                                            </FieldWrappers>
                                            {errors.mobile && touched.mobile && (
                                                <ErrorText>{errors.mobile}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Client Alternate Mobile */}
                                        <InputGroup>
                                            <Label htmlFor="alternateMobile">Alt. Mobile No. *</Label>
                                            <FieldWrappers>
                                                <MobileIcon />
                                                <Field
                                                    type="text"
                                                    id="alternateMobile"
                                                    name="alternateMobile"
                                                    className="w-full truncate pl-5"
                                                    placeholder="Enter 10-digit mobile number"
                                                    value={values.alternateMobile}
                                                    maxLength={10}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d{0,10}$/.test(value)) {
                                                            setFieldValue("alternateMobile", value);
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                />
                                            </FieldWrappers>
                                            {errors.alternateMobile && touched.alternateMobile && (
                                                <ErrorText>{errors.alternateMobile}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Client Budget */}
                                        <InputGroup>
                                            <Label htmlFor="budget">Budget (₹) *</Label>
                                            <FieldWrappers>
                                                <span className="absolute left-3 text-gray-500">₹</span>
                                                <Field
                                                    type="text"
                                                    name="budget"
                                                    id="budget"
                                                    className="w-full truncate pl-7"
                                                    placeholder="Enter budget amount"
                                                    min="1"
                                                    value={values.budget}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </FieldWrappers>
                                            {errors.budget && touched.budget && (
                                                <ErrorText>{errors.budget}</ErrorText>
                                            )}
                                        </InputGroup>

                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={() => {
                                        // resetForm();
                                        handleClose();
                                    }}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit">
                                        {editDealBasicLoading ? "Updating..." : "Update"}
                                        {/* {editDealBasicLoading ? "Updating..." : "Update"} */}
                                    </SubmitBtn>
                                </ActionSection>

                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;

export default EditBasicInfo;